import type { CategoryView } from '../../../../../../services/categories/categories.service';
import type { ProductView } from '../../../../../../services/products/products.service';
import { compareCategoryNamesForDisplay } from '../../../../../../services/categories/category-order.util';
import { buildChildrenByParent } from '../../../orders/menu/category-menu.utils';

// Summary: Categories sorted A to Z with the shared Hebrew/category ordering helper.
// Details: docs/function-library/features/category-report-group/category-report-group.md#category-manager-flow
export function sortCategoriesForManager(categories: CategoryView[]): CategoryView[] {
  return [...categories].sort((a, b) => compareCategoryNamesForDisplay(a.name, b.name));
}

// Summary: Manager reorder list — persisted display order (sortOrder), name as the tiebreak.
export function sortCategoriesByDisplayOrder(categories: CategoryView[]): CategoryView[] {
  const byId = new Map(categories.map((category) => [category.id, category]));
  const childrenByParent = new Map<number | null, CategoryView[]>();
  for (const category of categories) {
    const parentId = category.parentCategoryPk;
    const normalizedParent = parentId === null || !byId.has(parentId) ? null : parentId;
    const siblings = childrenByParent.get(normalizedParent) ?? [];
    siblings.push(category);
    childrenByParent.set(normalizedParent, siblings);
  }

  const sortSiblings = (items: CategoryView[]): CategoryView[] =>
    [...items].sort((a, b) => a.sortOrder - b.sortOrder || compareCategoryNamesForDisplay(a.name, b.name));

  const ordered: CategoryView[] = [];
  const visited = new Set<number>();
  const appendSubtree = (parentId: number | null): void => {
    const siblings = sortSiblings(childrenByParent.get(parentId) ?? []);
    for (const sibling of siblings) {
      if (visited.has(sibling.id)) {
        continue;
      }
      visited.add(sibling.id);
      ordered.push(sibling);
      appendSubtree(sibling.id);
    }
  };

  appendSubtree(null);

  // Safety net for inconsistent legacy rows (e.g. stale parent ids): never drop categories.
  for (const category of sortSiblings(categories)) {
    if (!visited.has(category.id)) {
      ordered.push(category);
    }
  }

  return ordered;
}

// Summary: Product ids assigned to a category seed the membership checkboxes.
// Details: docs/function-library/features/category-report-group/category-report-group.md#category-manager-flow
export function productIdsInCategory(products: ProductView[], categoryId: number): number[] {
  return products.filter((product) => product.categoryId === categoryId).map((product) => product.id);
}

const MAX_CATEGORY_DEPTH = 3;

// Summary: depth of a category counting itself as 1 (top-level = 1, sub = 2, sub-sub = 3).
// Details: docs/function-library/features/category-hierarchy/category-hierarchy.md#parent-picker
export function depthOf(categoryId: number, byId: Map<number, CategoryView>): number {
  let depth = 1;
  let cursor = byId.get(categoryId)?.parentCategoryPk ?? null;
  while (cursor !== null) {
    depth += 1;
    cursor = byId.get(cursor)?.parentCategoryPk ?? null;
  }
  return depth;
}

function isDescendantOf(candidateId: number, ancestorId: number, byId: Map<number, CategoryView>): boolean {
  let cursor = byId.get(candidateId)?.parentCategoryPk ?? null;
  while (cursor !== null) {
    if (cursor === ancestorId) return true;
    cursor = byId.get(cursor)?.parentCategoryPk ?? null;
  }
  return false;
}

// Summary: How many additional levels exist below categoryId in its current position (0 for a leaf).
function subtreeHeightOf(categoryId: number, byId: Map<number, CategoryView>): number {
  const childrenByParent = buildChildrenByParent(Array.from(byId.values()));
  let maxHeight = 0;
  const queue: { id: number; height: number }[] = [{ id: categoryId, height: 0 }];
  while (queue.length > 0) {
    const { id, height } = queue.shift() as { id: number; height: number };
    maxHeight = Math.max(maxHeight, height);
    for (const childId of childrenByParent.get(id) ?? []) {
      queue.push({ id: childId, height: height + 1 });
    }
  }
  return maxHeight;
}

// Summary: Parent-picker options — excludes self, own descendants (cycle), max-depth categories,
// and any parent that would push the moved category's existing subtree past the depth cap.
// Details: docs/function-library/features/category-hierarchy/category-hierarchy.md#parent-picker
export function getSelectableParentOptions(
  categories: CategoryView[],
  excludeCategoryId: number | null,
): { value: string; label: string }[] {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const subtreeHeight = excludeCategoryId === null ? 0 : subtreeHeightOf(excludeCategoryId, byId);
  return categories
    .filter((c) => c.id !== excludeCategoryId)
    .filter((c) => excludeCategoryId === null || !isDescendantOf(c.id, excludeCategoryId, byId))
    .filter((c) => depthOf(c.id, byId) + subtreeHeight < MAX_CATEGORY_DEPTH)
    .map((c) => ({ value: String(c.id), label: c.name }));
}
